import axios from 'axios';
import { IconAlertTriangle } from 'featherico';
import { some } from 'lodash/fp';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../../../../app-config/constants';
import api from '../../../api';
import { logout } from '../../../api/custom/UserLogout';
import { schedulePageReload } from '../../../routing/history';
import Button from '../../components/Button';
import { useAppContext } from '../../hooks/useAppContext';
import useEventBus, { EventType } from '../../hooks/useEventBus';
import { goToUrl } from '../../services/Router';
import { trackEvent } from '../../services/Tracking';
import { getLoginUrl, getLogoutRedirectUrl } from '../../services/User';
import UserLoggedOutModal from './UserLoggedOutModal';
import { UserSessionStatus } from './types';

const MINUTE = 60000;
const DAY = MINUTE * 60 * 24;
const CHECK_PERIOD = MINUTE * 5;
const OUTDATED_VERSION_WARNING_DELAY = DAY * 3;

type BannerProps = { show: boolean };

const WarningBanner: React.FC<React.PropsWithChildren<BannerProps & { gaLabel: string }>> = ({
    children,
    show,
    gaLabel,
}) => {
    useEffect(() => {
        if (show) {
            trackEvent({
                category: 'SYSTEM',
                label: gaLabel,
                nonInteractive: true,
            });
        }
    }, [show, gaLabel]);

    if (!show) {
        return null;
    }

    return (
        <div className="boxWarning broadcast">
            <div>
                <IconAlertTriangle /> {children}
            </div>
        </div>
    );
};

const OutdatedVersionBanner: React.FC<BannerProps> = ({ show }) => (
    <WarningBanner show={show} gaLabel={'Show "outdated version" banner'}>
        Your version of Small Improvements is outdated. Please{' '}
        <Button kind="LINK" onClick={() => window.location.reload()}>
            refresh this page
        </Button>{' '}
        to see the newest version.
    </WarningBanner>
);

const LoggedOutBanner: React.FC<BannerProps> = ({ show }) => (
    <WarningBanner show={show} gaLabel={'Show "logged out" banner'}>
        You’ve been logged out. Please make a copy of unsaved content before you leave this page or{' '}
        <a href={getLoginUrl()}>log in again</a>.
    </WarningBanner>
);

const PermissionsChangedBanner: React.FC<BannerProps> = ({ show }) => {
    const { user } = useAppContext();
    return (
        <WarningBanner show={show} gaLabel={'Show "permissions changed" banner'}>
            Your permissions within Small Improvements have changed. You need to{' '}
            <Button
                onClick={() => {
                    logout().then(() => goToUrl(getLogoutRedirectUrl(user)));
                }}
                kind="LINK"
            >
                logout
            </Button>{' '}
            and log in again to apply the new permissions.
        </WarningBanner>
    );
};

const useSystemStatus = () => {
    const { user } = useAppContext();
    const [state, setState] = useState({
        isLoggedIn: true,
        permissionsChanged: false,
        serverRev: __currentRev__,
        recievedUnauthorizedApiResponse: false,
    });
    useEventBus(EventType.UnauthorizedApiResponse, () => {
        setState({ ...state, recievedUnauthorizedApiResponse: true });
    });

    useEffect(() => {
        let isMounted = true;
        let isFetching = false;
        let timeoutId: number | undefined;

        const isVisible = () => document.visibilityState === 'visible';

        const handleSystemStatus = (status: UserSessionStatus) => {
            if (!isMounted) {
                return;
            }
            if (!status) {
                return setState({
                    isLoggedIn: false,
                    serverRev: __currentRev__,
                    permissionsChanged: false,
                    recievedUnauthorizedApiResponse: true,
                });
            }
            setState({
                isLoggedIn: true,
                serverRev: status.serverRev || __currentRev__,
                permissionsChanged: some(Boolean, [
                    status.roles.isHR !== user.isHR,
                    status.roles.isAssistant !== user.isAssistant,
                    status.roles.isAdmin !== user.isAdmin,
                ]),
                recievedUnauthorizedApiResponse: false,
            });
        };

        const fetchSystemStatus = async () => {
            isFetching = true;
            try {
                const { data: status } = await axios.get<UserSessionStatus>(`${API_URL}/users/${user.id}/isLoggedIn`);
                if (!status) {
                    // If the user appears to be logged out, we try to renew the session.
                    try {
                        await api.currentUser.invalidate();
                        await api.currentUser.get(); // Try logging in through remember-me and update the current user.
                        const { data: loggedInStatus } = await axios.get<UserSessionStatus>(
                            `${API_URL}/users/${user.id}/isLoggedIn`
                        );
                        handleSystemStatus(loggedInStatus); // We successfully renewed the session through remember-me.
                    } catch {
                        // We could not renew the existing session, which means the user is actually logged out.
                        handleSystemStatus(null);
                    }
                } else {
                    // The user is still logged in, so we register any updates.
                    handleSystemStatus(status);
                }
            } catch {
                // No need to handle network or server errors.
            } finally {
                isFetching = false;
                if (isVisible() && isMounted) {
                    timeoutId = window.setTimeout(fetchSystemStatus, CHECK_PERIOD);
                }
            }
        };

        const handleStatusChange = () => {
            window.clearTimeout(timeoutId);
            if (isVisible() && !isFetching) {
                fetchSystemStatus();
            }
        };

        document.addEventListener('visibilitychange', handleStatusChange);

        if (isVisible()) {
            timeoutId = window.setTimeout(fetchSystemStatus, CHECK_PERIOD);
        }

        if (state.recievedUnauthorizedApiResponse) {
            handleStatusChange();
        }

        return () => {
            isMounted = false;
            window.clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', handleStatusChange);
        };
    }, [user.id, user.isHR, user.isAdmin, user.isAssistant, state.recievedUnauthorizedApiResponse]);

    return state;
};

const useShouldShowOutdatedVersionBanner = (serverRev: string) => {
    const [showBanner, setShowBanner] = useState(false);
    const timeoutRef = useRef<number | undefined>();

    useEffect(() => {
        if (serverRev === __currentRev__) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        } else if (!timeoutRef.current) {
            timeoutRef.current = window.setTimeout(() => setShowBanner(true), OUTDATED_VERSION_WARNING_DELAY);
        }
    }, [serverRev]);

    useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    return showBanner;
};

const useModalState = (baseCondition: boolean, onCloseTrigger: () => void, gaLabel: string): [boolean, () => void] => {
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (baseCondition) {
            setShowModal(true);
            trackEvent({
                category: 'SYSTEM',
                label: gaLabel,
                nonInteractive: true,
            });
        }
    }, [baseCondition, gaLabel]);

    const onClose = useCallback(() => {
        setShowModal(false);
        onCloseTrigger();
    }, [onCloseTrigger]);

    return [baseCondition && showModal, onClose];
};

const useSchedulePageReloadOnVersionChange = (serverRev: string) => {
    useEffect(() => {
        const isVersionUpToDate = serverRev === __currentRev__;
        if (isVersionUpToDate) {
            return;
        }
        schedulePageReload();
        trackEvent({
            category: 'SYSTEM',
            label: `Version has changed: ${__currentRev__} <> ${serverRev}`,
            action: 'Scheduled page reload',
            nonInteractive: true,
        });
    }, [serverRev]);
};

const UserSessionWarnings: React.FC = () => {
    const { isLoggedIn, permissionsChanged, serverRev } = useSystemStatus();
    const showOutdatedVersionBanner = useShouldShowOutdatedVersionBanner(serverRev);
    const [showLoggedOutModal, closeLoggedOutModal] = useModalState(
        !isLoggedIn,
        schedulePageReload,
        `Show 'logged out' modal`
    );
    useSchedulePageReloadOnVersionChange(serverRev);
    return (
        <>
            <LoggedOutBanner show={!isLoggedIn} />
            <OutdatedVersionBanner show={showOutdatedVersionBanner} />
            <PermissionsChangedBanner show={permissionsChanged} />
            <UserLoggedOutModal isOpen={showLoggedOutModal} onClose={closeLoggedOutModal} />
        </>
    );
};

export default UserSessionWarnings;
